import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

const Toggle = ({ checked, onChange, disabled }) => (
  <label className="toggleSwitch">
    <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
    <span className="toggleSlider" />
  </label>
);

// Stock tab of Product Management. A product is counted only when the restaurant-wide switch AND its
// own "Track stock" toggle are on; every POS and website sale then takes units off, and the product
// shows as sold out at zero until quantity is added here. The same numbers back the admin's Stock page.
const StockPanel = ({ searchTerm }) => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [products, setProducts] = useState([]);
  const [trackedOnly, setTrackedOnly] = useState(false);
  const [drafts, setDrafts] = useState({}); // id -> typed quantity not yet saved
  const [addDrafts, setAddDrafts] = useState({}); // id -> typed "add" amount

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callAPI(apiUrls.PRODUCT_MANAGEMENT_STOCK, {}, 'GET');
      setEnabled(!!res.data?.data?.enabled);
      setProducts(res.data?.data?.products || []);
    } catch (err) {
      console.error('Failed to load stock', err);
      toast.error('Failed to load stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (id, fields) =>
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, ...fields } : p)));

  const save = async (product, body, okMessage) => {
    const previous = { stock_tracking: product.stock_tracking, stock_quantity: product.stock_quantity, out_of_stock: product.out_of_stock };
    try {
      const res = await callAPI(apiUrls.PRODUCT_MANAGEMENT_UPDATE_STOCK, {}, 'POST', { id: product.id, ...body }, {});
      const saved = res.data?.data;
      if (saved) {
        patch(product.id, {
          stock_tracking: saved.stock_tracking,
          stock_quantity: saved.stock_quantity,
          out_of_stock: saved.out_of_stock,
        });
      }
      if (okMessage) toast.success(okMessage);
      return true;
    } catch (err) {
      console.error('Failed to update stock', err);
      toast.error('Failed to update stock');
      patch(product.id, previous);
      return false;
    }
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    try {
      await callAPI(apiUrls.PRODUCT_MANAGEMENT_SET_STOCK_ENABLED, {}, 'POST', { enabled: next ? 1 : 0 }, {});
      toast.success(next ? 'Stock tracking turned on' : 'Stock tracking turned off');
      load();
    } catch (err) {
      console.error('Failed to change stock tracking', err);
      toast.error('Failed to change stock tracking');
      setEnabled(!next);
    }
  };

  const toggleTracking = (product) => {
    const next = !product.stock_tracking;
    patch(product.id, { stock_tracking: next });
    save(product, { tracking: next ? 1 : 0 }, `${product.name}: stock tracking ${next ? 'on' : 'off'}`);
  };

  const commitQuantity = async (product) => {
    const typed = drafts[product.id];
    if (typed === undefined) return;
    const clear = () => setDrafts((d) => { const n = { ...d }; delete n[product.id]; return n; });
    const qty = parseInt(typed, 10);
    if (Number.isNaN(qty) || qty < 0) {
      toast.error('Enter a quantity of 0 or more.');
      clear();
      return;
    }
    clear();
    if (qty !== product.stock_quantity) await save(product, { quantity: qty }, `${product.name}: quantity set to ${qty}`);
  };

  const commitAdd = async (product) => {
    const amount = parseInt(addDrafts[product.id], 10);
    if (Number.isNaN(amount) || amount === 0) return;
    setAddDrafts((d) => { const n = { ...d }; delete n[product.id]; return n; });
    await save(product, { add: amount }, `${product.name}: ${amount > 0 ? 'added' : 'removed'} ${Math.abs(amount)}`);
  };

  const term = (searchTerm || '').trim().toLowerCase();
  const visible = products
    .filter((p) => !term || p.name?.toLowerCase().includes(term))
    .filter((p) => !trackedOnly || p.stock_tracking);

  const statusFor = (p) => {
    if (!p.stock_tracking) return { label: 'Not tracked', cls: 'stockStatus stockNone' };
    if (!enabled) return { label: 'Tracking off (restaurant)', cls: 'stockStatus stockNone' };
    return p.out_of_stock
      ? { label: 'Out of stock - sold out', cls: 'stockStatus stockOut' }
      : { label: 'In stock', cls: 'stockStatus stockIn' };
  };

  if (loading) return <p>Loading stock...</p>;

  return (
    <div className="printerCard" style={{ overflowX: 'auto' }}>
      <div className={`stockBanner ${enabled ? 'on' : 'off'}`}>
        <div>
          <strong>Stock tracking for this restaurant: {enabled ? 'ON' : 'OFF'}</strong>
          <div className="stockBannerHint">
            {enabled
              ? 'Every POS and website sale of a tracked product takes units off. At zero the product shows as sold out until you add quantity here.'
              : 'Turn this on for tracked products to count. Until then quantities below are saved but not used.'}
          </div>
        </div>
        <Toggle checked={enabled} onChange={toggleEnabled} />
      </div>

      <div className="d-flex justify-content-end mb-2">
        <label className="d-flex align-items-center gap-2 mb-0">
          <input type="checkbox" checked={trackedOnly} onChange={(e) => setTrackedOnly(e.target.checked)} />
          Show tracked products only
        </label>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Category</th>
            <th>Track stock</th>
            <th>Quantity</th>
            <th>Add stock</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((product) => {
            const status = statusFor(product);
            return (
              <tr key={product.id}>
                <td>{product.name}{product.status !== 1 && <span className="stockHidden"> (hidden)</span>}</td>
                <td>{product.category_name || '-'}</td>
                <td>
                  <Toggle checked={!!product.stock_tracking} onChange={() => toggleTracking(product)} />
                </td>
                <td>
                  <input
                    type="number"
                    min="0"
                    className="form-control form-control-sm stockQtyInput"
                    value={drafts[product.id] !== undefined ? drafts[product.id] : product.stock_quantity}
                    onChange={(e) => setDrafts((d) => ({ ...d, [product.id]: e.target.value }))}
                    onBlur={() => commitQuantity(product)}
                    onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                    aria-label={`Quantity of ${product.name}`}
                  />
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <input
                      type="number"
                      className="form-control form-control-sm stockAddInput"
                      placeholder="+ qty"
                      value={addDrafts[product.id] ?? ''}
                      onChange={(e) => setAddDrafts((d) => ({ ...d, [product.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && commitAdd(product)}
                      aria-label={`Add stock to ${product.name}`}
                    />
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => commitAdd(product)}>
                      Add
                    </button>
                  </div>
                </td>
                <td><span className={status.cls}>{status.label}</span></td>
              </tr>
            );
          })}
          {visible.length === 0 && (
            <tr><td colSpan={6}>No products found</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default StockPanel;
